import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Markdown } from '../components/markdown/markdown';
import './CanvasChat.css';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';

// 文件上传常量
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
const MAX_FILE_COUNT = 5;
const ALLOWED_FILE_TYPES = [
  'text/plain', 'text/markdown', 'text/html',
  'application/pdf', 'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'
];
const ALLOWED_FILE_EXTENSIONS = [
  '.txt', '.md', '.mdx', '.markdown', '.pdf', '.html',
  '.xlsx', '.xls', '.docx', '.csv',
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'
];

// 定义上传文件接口
interface UploadedFile {
  temp_file_id: string;
  minio_path: string;
  file_name: string;
  file_type: string;
}

// 定义消息类型接口
interface Message {
  type: 'user' | 'log';  // 用户消息或思考过程
  content: string;
  timestamp?: string;
  files?: UploadedFile[]; // 添加文件数组字段
}

// 定义API响应数据接口
interface ApiResponse {
  type: 'log' | 'result';  // log为思考过程，result为最终结果
  timestamp?: string;
  content: string;
  uuid?: string; // 添加uuid字段
}

// 组件属性接口
interface CanvasChatProps {
  appId?: string | null;
  standaloneMode?: boolean; // 是否为独立模式，默认为true
}

/**
 * Gemini风格聊天与编辑器组件
 * 左侧聊天窗口显示思考过程，右侧Markdown编辑器显示最终结果
 */
const CanvasChat: React.FC<CanvasChatProps> = ({ appId: propAppId, standaloneMode = true }) => {
  const { t, i18n } = useTranslation();
  // 从URL中获取app_id参数，如果没有传入属性，则使用URL参数
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { publishUuid, userUuid } = useParams<{ publishUuid: string; userUuid: string }>();
  const urlAppId = searchParams.get('app_id');
  // 优先使用属性传入的appId，如果没有则使用URL参数
  const appId = propAppId || urlAppId;
  
  // 判断当前是否为run模式（通过publishUuid访问）
  const isRunMode = !!publishUuid;
  
  // 聊天相关状态
  const [inputText, setInputText] = useState<string>(''); // 输入文本
  const [isLoading, setIsLoading] = useState<boolean>(false); // 加载状态
  const [error, setError] = useState<string | null>(null); // 错误信息
  const [messages, setMessages] = useState<Message[]>([]);
  
  // 编辑器相关状态
  const [editorContent, setEditorContent] = useState<string>(''); // 编辑器内容（最终结果）
  const [documentTitle, setDocumentTitle] = useState<string>(t('apps.detail.untitledDoc', '未命名文档')); // 文档标题
  
  // 添加应用名称状态
  const [appName, setAppName] = useState<string>('');
  
  const messagesEndRef = useRef<HTMLDivElement>(null); // 用于聊天自动滚动

  // 文件上传相关状态
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 添加状态存储uuid
  const [resultUuid, setResultUuid] = useState<string | null>(null);

  // 添加拷贝成功状态
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  // 添加预览模式状态
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(true);

  // 添加导出PDF相关状态
  const [isExportingPDF, setIsExportingPDF] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string>('');

  // 处理聊天框内容滚动
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // 确保组件初始化时右侧内容为空
  useEffect(() => {
    setEditorContent('');
    setResultUuid(null);
    setCopySuccess(false);
    setIsPreviewMode(true);
  }, []);

  // 获取发布应用的名称
  useEffect(() => {
    if (publishUuid) {
      const fetchAppName = async () => {
        try {
          // 根据环境使用不同的域名
          const baseUrl = window.location.hostname === 'ceshi.lightark.cc' 
            ? 'https://ceshi.lightark.cc' 
            : 'https://ceshi.lightark.cc';
            
          const response = await fetch(`${baseUrl}/api/v1/apps/publish/${publishUuid}/name`);
          if (response.ok) {
            const data = await response.json();
            setAppName(data.app_name);
            // 更新文档标题
            setDocumentTitle(data.app_name);
            // 更新页面标题
            document.title = data.app_name;
          }
        } catch (error) {
          console.error('获取应用名称失败:', error);
        }
      };
      
      fetchAppName();
    }
  }, [publishUuid]);

  // 当组件加载时，如果有appId参数，则设置标题
  useEffect(() => {
    if (appId) {
      setDocumentTitle(`${t('apps.detail.app', '应用')} #${appId}`);
    }
  }, [appId, t]);
  
  // 添加和移除body类名以控制滚动条
  useEffect(() => {
    // 添加类名控制滚动条
    document.body.classList.add('canvas-chat-page');
    
    // 组件卸载时移除类名
    return () => {
      document.body.classList.remove('canvas-chat-page');
    };
  }, []);

  // 处理窗口大小改变，确保响应式布局正确切换
  useEffect(() => {
    const handleResize = () => {
      // 确保文本框在布局切换后仍然可见
      const chatTextarea = document.querySelector('.chat-textarea') as HTMLTextAreaElement;
      if (chatTextarea) {
        // 重置文本框高度
        chatTextarea.style.height = 'auto';
        const newHeight = Math.min(chatTextarea.scrollHeight, 300);
        chatTextarea.style.height = `${newHeight}px`;
      }
    };

    // 添加事件监听器
    window.addEventListener('resize', handleResize);
    
    // 组件卸载时移除事件监听器
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /**
   * 处理文件上传按钮点击
   */
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * 处理文件上传
   * @param event 文件输入事件
   */
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    await uploadFile(files[0]);
    // 清空输入以确保同一文件可以再次选择
    if (event.target) {
      event.target.value = '';
    }
  };

  /**
   * 上传单个文件
   * @param file 要上传的文件
   */
  const uploadFile = async (file: File) => {
    // 检查文件数量限制
    if (uploadedFiles.length >= MAX_FILE_COUNT) {
      setUploadError(t('apps.detail.maxFiles', `最多只能上传${MAX_FILE_COUNT}个文件`));
      return;
    }

    // 获取文件扩展名
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    // 验证文件类型和扩展名
    if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      setUploadError(t('apps.detail.unsupportedFileType', '不支持的文件类型。请上传以下格式的文件：txt、markdown、md、mdx、pdf、html、xlsx、xls、docx、csv、jpg、jpeg、png、gif、webp、svg'));
      return;
    }

    // 验证文件大小
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(t('apps.detail.fileSizeLimit', '文件大小不能超过15MB'));
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      // 根据环境使用不同的域名
      const baseUrl = window.location.hostname === 'ceshi.lightark.cc' 
        ? 'https://ceshi.lightark.cc' 
        : 'https://ceshi.lightark.cc';

      const response = await axios.post<UploadedFile>(
        `${baseUrl}/api/v1/files/upload/temp`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      setUploadedFiles(prev => [...prev, {
        ...response.data,
        file_name: file.name,
        file_type: file.type
      }]);
    } catch (err) {
      setUploadError(t('apps.detail.uploadFailed', '文件上传失败，请重试'));
      console.error('文件上传失败:', err);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * 处理删除上传的文件
   * @param tempFileId 文件ID
   */
  const handleRemoveFile = (tempFileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.temp_file_id !== tempFileId));
  };

  /**
   * 获取文件图标 - ChatGPT风格的简洁字符
   * @param fileType 文件类型
   */
  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return 'PDF';
    if (fileType.includes('word')) return 'DOC';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'XLS';
    if (fileType.includes('image')) return 'IMG';
    if (fileType.includes('text') || fileType.includes('markdown')) return 'TXT';
    return 'FILE';
  };

  /**
   * 渲染上传的文件列表
   */
  const renderUploadedFiles = () => {
    if (uploadedFiles.length === 0) return null;

    return (
      <div className="uploaded-files">
        {uploadedFiles.map((file) => (
          <div key={file.temp_file_id} className="file-item">
            <div className="file-icon">{getFileIcon(file.file_type)}</div>
            <div className="file-name">{file.file_name}</div>
            <button
              className="remove-file-button"
              onClick={() => handleRemoveFile(file.temp_file_id)}
              aria-label="删除文件"
              type="button"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    );
  };

  /**
   * 处理聊天表单提交 - 修改为包含文件ID
   * @param e 表单事件对象
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() && uploadedFiles.length === 0) return;

    try {
      // 保存当前输入和文件用于发送
      const currentInput = inputText;
      const currentFiles = [...uploadedFiles];
      
      // 立即清空输入框和文件列表，重置文本框状态
      setInputText('');
      setUploadedFiles([]);
      
      // 重置文本框高度
      const textarea = document.querySelector('.chat-textarea') as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.height = '52px'; // 重置为最小高度
      }
      
      // 清空之前的状态 - 确保右侧编辑器内容被完全清空
      setMessages([]); // 清空之前的消息
      setEditorContent(''); // 清空编辑器内容
      setResultUuid(null); // 清空之前的uuid
      setError(null); // 清空错误信息
      setIsPreviewMode(true); // 重置为预览模式
      setCopySuccess(false); // 重置拷贝状态
      
      // 格式化时间
      const now = new Date();
      const timestamp = `${now.toLocaleString('default', { month: 'short' })} ${now.getDate()}, ${now.toLocaleString('default', { hour: 'numeric', minute: 'numeric', hour12: true })}`;
      
      // 添加用户消息，使用保存的输入和文件
      setMessages(prevMessages => [...prevMessages, { 
        type: 'user', 
        content: currentInput,
        timestamp,
        files: currentFiles
      }]);
      
      // 设置加载状态
      setIsLoading(true);
      
      // 模拟数据（用于测试，实际环境中应删除）
      const mockResponse = currentInput.toLowerCase().includes('mock');
      
      if (mockResponse) {
        // 模拟响应数据，用于测试
        await processMockResponse(timestamp);
      } else {
        // 实际API调用，传递文件ID（使用保存的文件列表）
        await processApiResponse(currentInput, timestamp, currentFiles);
      }
    } catch (err) {
      console.error('请求错误:', err);
      setError(err instanceof Error ? err.message : '未知错误');
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * 处理模拟响应数据
   * @param timestamp 时间戳
   */
  const processMockResponse = async (timestamp: string) => {
    // 模拟数据
    const mockData = [
      { type: 'log', timestamp: '2025-05-08T18:50:22.217375', content: 'ResumeGenerationAgent@0a030ac8f8c115c17b608730e5b197c8fa4d9d59da9f5709c1eb704800238245 模块加载完成, 开始执行 CrewBase(ResumeGenerationAgentCrew)' },
      { type: 'log', timestamp: '2025-05-08 10:50:22', content: '任务: resume_generation_task\n描述: 根据用户提供的学术和职业历史信息，生成一份高质量、格式规范的个人简历。请确保在生成简历时，考虑到以下要求： 1. 生成java简历 包含了学术和职业经历的详细信息，确保在简历中准确反映。 2. 在处理信息时，要特别注意避免职业经历与学术背景的重叠，确保简历内容清晰且不重复。 3. 对于特定行业的需求（如简历格式、关键词要求），请参考行业标准进行调整，以确保符合招聘系统的自动筛选标准。 4. 如果用户提供的信息不完整或存在错误，请适当提醒并处理，确保简历质量。 5. 在多语言支持方面，确保简历在不同语言环境下的显示效果正确，特别是在英文和中文版本的处理上要特别注意。\n\n执行者: 简历生成专家\n\n状态: started\n输出: ' },
      { type: 'log', timestamp: '2025-05-08 10:50:37', content: '任务: resume_generation_task\n描述: 根据用户提供的学术和职业历史信息，生成一份高质量、格式规范的个人简历。请确保在生成简历时，考虑到以下要求： 1. 生成java简历 包含了学术和职业经历的详细信息，确保在简历中准确反映。 2. 在处理信息时，要特别注意避免职业经历与学术背景的重叠，确保简历内容清晰且不重复。 3. 对于特定行业的需求（如简历格式、关键词要求），请参考行业标准进行调整，以确保符合招聘系统的自动筛选标准。 4. 如果用户提供的信息不完整或存在错误，请适当提醒并处理，确保简历质量。 5. 在多语言支持方面，确保简历在不同语言环境下的显示效果正确，特别是在英文和中文版本的处理上要特别注意。\n\n执行者: 简历生成专家\n\n状态: completed\n输出: ' },
      { type: 'log', timestamp: '2025-05-08 10:50:37', content: '任务: resume_optimization_task\n描述: Based on the provided context, optimize the generated resume to ensure it not only accurately reflects the user\'s professional and academic history but also has an appealing visual design and highlights the user\'s strengths.  Focus on addressing potential issues such as: - Inconsistent formatting between different sections (e.g., education, work experience) - Missing industry-specific keywords or skills - Redundant information that dilutes key points - Crowded layout due to too much content - Lack of personalization or uniqueness The resume should be streamlined, error-free, and tailored to the specific job or industry the user is targeting.\n\n执行者: 简历优化专家\n状态: started\n输出: ' },
      { type: 'log', timestamp: '2025-05-08 10:50:53', content: '任务: resume_optimization_task\n描述: Based on the provided context, optimize the generated resume to ensure it not only accurately reflects the user\'s professional and academic history but also has an appealing visual design and highlights the user\'s strengths.  Focus on addressing potential issues such as: - Inconsistent formatting between different sections (e.g., education, work experience) - Missing industry-specific keywords or skills - Redundant information that dilutes key points - Crowded layout due to too much content - Lack of personalization or uniqueness The resume should be streamlined, error-free, and tailored to the specific job or industry the user is targeting.\n\n执行者: 简历优化专家\n状态: completed\n输出: ' },
      { type: 'result', content: '---\n\n**简历**\n\n---\n\n### **个人信息**\n- **姓名**：[请输入您的姓名]\n- **联系电话**：[请输入您的联系电话]\n- **电子邮件**：[请输入您的电子邮件]\n- **LinkedIn**：[请输入您的LinkedIn链接（如有）]\n- **GitHub**：[请输入您的GitHub链接（如有）]\n- **居住地**：[请输入您的居住地]\n\n---\n\n### **教育背景**\n- **[学位名称]**，**[专业名称]**  \n  **[学校名称]** — [毕业年份]  \n  主要课程：数据结构与算法、Java编程、软件工程、数据库管理、面向对象设计等  \n  荣誉与奖项： [列出相关的学术成就或荣誉]\n\n---\n\n### **工作经验**\n\n**[公司名称]** — **[职位名称]**  \n*工作时间段：[起始年月] - [结束年月]*  \n- 通过优化后台系统中的Java应用程序，提升了系统性能20%，加速了数据处理速度。\n- 设计并实现了高效的RESTful API，改善了前后端数据交互的速度与稳定性。\n- 负责数据库设计及优化，采用MySQL和Redis，确保数据高可用与高性能。\n- 定期进行代码审查和技术培训，提升团队的技术水平和开发效率。\n\n**[公司名称]** — **[职位名称]**  \n*工作时间段：[起始年月] - [结束年月]*  \n- 在敏捷环境中开发并维护多个Java项目，确保项目按时交付并满足客户需求。\n- 负责监控与优化生产环境中的应用程序，解决了多个关键性能瓶颈。\n- 使用JUnit进行单元测试，提高了项目的稳定性和代码质量。\n\n---\n\n### **技能概述**\n- **编程语言**：Java, Python, JavaScript, SQL  \n- **框架与工具**：Spring Boot, Hibernate, JUnit, Maven, Git, Docker  \n- **数据库管理**：MySQL, PostgreSQL, Redis  \n- **操作系统**：Linux, Windows  \n- **开发方法**：敏捷开发, TDD（测试驱动开发）  \n- **其他技能**：RESTful API设计, 微服务架构, 云计算 (AWS, Docker)\n\n---\n\n### **项目经历**\n\n**项目名称：[项目名称]**  \n*项目时间：[起始年月] - [结束年月]*  \n- 设计并实现了基于Spring Boot的多模块后台系统，采用MySQL数据库，优化了高并发处理能力。\n- 负责用户认证、数据加密及安全通信，确保项目符合隐私保护法规。\n- 实现CI/CD流程，使用Jenkins进行自动化部署，显著提高了开发效率。\n\n**项目名称：[项目名称]**  \n*项目时间：[起始年月] - [结束年月]*  \n- 开发了一个自动化测试框架，通过Selenium和JUnit提升了测试覆盖率与测试效率。\n- 与前端团队协作，优化API的性能，解决了多个跨平台兼容性问题。\n\n---\n\n### **附加信息**  \n- **兴趣爱好**：编程、开源贡献、篮球、旅行  \n- **推荐人**：  \n  [推荐人姓名] — [职位]  \n  [推荐人公司]  \n  [推荐人联系方式]\n\n---\n\n**优化建议与说明**：\n\n1. **格式一致性**：所有职位、教育背景以及项目经历的布局统一，确保简历的视觉一致性。\n2. **去除冗余信息**：简化了一些过于重复的描述，集中突出了核心技能和成就。\n3. **针对性增强**：根据目标职位和行业需求，特别强调了Java、数据库优化以及敏捷开发等相关技能。\n4. **排版优化**：将简历的各个部分进行了合理划分，并增强了信息的层次感，避免了内容过于拥挤。\n5. **行业关键词**：加入了与目标职位相关的行业术语和技术栈（如Spring Boot、CI/CD、RESTful API等），有助于简历通过招聘系统筛选。\n\n最终结果：该简历以清晰、精简和专业的格式展示了用户的核心优势和技能，突出了与目标职位最相关的经验，并以简洁的设计提高了视觉吸引力。' }
    ];
    
    // 模拟网络延迟
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    
    // 分批次发送模拟数据以模拟流式响应
    for (const data of mockData) {
      await delay(800);  // 每条消息间隔800毫秒
      if (data.type === 'log') {
        setMessages(prevMessages => {
          const hasLog = prevMessages.some(
            m => m.type === 'log' && m.content === data.content
          );
          
          if (hasLog) {
            return prevMessages;
          } else {
            return [...prevMessages, { 
              type: 'log', 
              content: data.content,
              timestamp: data.timestamp || timestamp
            }];
          }
        });
      } else if (data.type === 'result') {
        setEditorContent(data.content);
      }
    }
  };
  
  /**
   * 处理实际API响应 - 修改为包含文件ID
   * @param inputText 用户输入的文本
   * @param timestamp 时间戳
   */
  const processApiResponse = async (inputText: string, timestamp: string, files: UploadedFile[]) => {
    // 获取当前用户ID
    const userId = localStorage.getItem('userId') || '19'; // 默认用户ID为19
    
    // 直接从i18n实例获取当前语言
    const currentLanguage = i18n.language || '';
    console.log('当前语言:', currentLanguage); // 添加日志便于调试
    
    // 如果语言代码以'zh'开头(如zh-CN)则使用'zh'，英文则使用'en'，默认为'zh'
    const language = currentLanguage.startsWith('zh') ? 'zh' : (currentLanguage.startsWith('en') ? 'en' : 'zh');
    console.log('API使用的语言参数:', language); // 添加日志便于调试
    
    // 准备请求体
    let requestBody;
    
    // 如果有publishUuid，使用uuid参数；否则使用app_id参数
    if (publishUuid) {
      requestBody = { 
        task_input: inputText,
        uuid: publishUuid,
        file_ids: files.map(file => file.temp_file_id), // 添加文件ID列表
        language: language, // 添加语言参数
        user_uuid: userUuid || 'default' // 添加user_uuid参数
      };
    } else {
      requestBody = { 
        task_input: inputText,
        app_id: appId ? parseInt(appId) : 123,
        user_id: parseInt(userId),
        file_ids: files.map(file => file.temp_file_id), // 添加文件ID列表
        language: language // 添加语言参数
      };
    }
    
    // 根据环境使用不同的域名
    const baseUrl = window.location.hostname === 'ceshi.lightark.cc' 
      ? 'https://ceshi.lightark.cc' 
      : 'https://ceshi.lightark.cc';
    
    // 调用API接口
    const response = await fetch(`${baseUrl}/api/v1/flow/run-flow`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ0ZXN0QHFxLmNvbSIsImV4cCI6MTc0NzU1NjE5N30.V-r0NO089krOoDpQoyBWtObcJ8ix2VNSaaPwdZhxhNg'}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error('请求失败');
    }

    // 创建reader读取流数据
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    
    if (reader) {
      let resultContent = ''; // 存储最终结果内容
      let lastTimestamp = timestamp; // 记录最后一条消息的时间戳
      let buffer = ''; // 用于处理不完整的数据块
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        // 解码并处理数据
        const text = decoder.decode(value);
        buffer += text;
        
        // 按行分割，处理每一行数据
        const lines = buffer.split('\n');
        // 最后一行可能不完整，保留到下一次处理
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          try {
            // 处理以 "data: " 开头的SSE数据
            const dataPrefix = "data: ";
            let jsonStr = line;
            
            if (line.startsWith(dataPrefix)) {
              jsonStr = line.substring(dataPrefix.length);
            }
            
            if (!jsonStr.trim()) continue;
            
            const data: ApiResponse = JSON.parse(jsonStr);
            
            // 提取时间戳，如果有
            const responseTimestamp = data.timestamp || lastTimestamp;
            lastTimestamp = responseTimestamp;
            
            if (data.type === 'log') {
              // 处理思考过程，显示在左侧聊天窗口
              setMessages(prevMessages => {
                // 寻找相同内容的log消息，避免重复添加
                const hasLog = prevMessages.some(
                  m => m.type === 'log' && m.content === data.content
                );
                
                if (hasLog) {
                  // 如果找到相同内容的消息，不重复添加
                  return prevMessages;
                } else {
                  // 否则添加新的log消息
                  return [...prevMessages, { 
                    type: 'log', 
                    content: data.content,
                    timestamp: responseTimestamp
                  }];
                }
              });
            } else if (data.type === 'result') {
              // 处理最终结果，显示在右侧编辑器
              resultContent = data.content;
              setEditorContent(resultContent);
              // 如果返回了uuid，保存它
              if (data.uuid) {
                setResultUuid(data.uuid);
              }
            }
          } catch (e) {
            console.error('解析响应数据失败:', e, 'line:', line);
          }
        }
      }
    }
  };
  
  /**
   * 处理返回到应用详情页
   * 从URL参数中获取workspace_id，如果不存在则使用默认值
   * 如果是通过publishUuid方式访问，则不显示返回按钮
   */
  const handleBackToDetail = () => {
    // 如果没有appId，说明是通过publishUuid访问的，不应该显示返回按钮
    if (!appId) return;
    
    // 从URL参数中获取workspace_id，而不是使用localStorage中的固定值
    const workspaceId = searchParams.get('workspace_id') || localStorage.getItem('workspace_id') || '34';
    navigate(`/apps/detail/${appId}?workspace_id=${workspaceId}`);
  };
  
  // 发送按钮内容
  const sendButtonContent = isLoading ? 
    <span className="loading-dots">●●●</span> : 
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 2L8 14M8 2L4 6M8 2L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>;

  // 修改预览功能处理函数，支持发布页面的预览
  const handlePreviewResult = () => {
    if (resultUuid) {
      // 根据环境使用不同的域名
      const baseUrl = window.location.hostname === 'ceshi.lightark.cc' 
        ? 'https://ceshi.lightark.cc' 
        : 'https://ceshi.lightark.cc';
      window.open(`${baseUrl}/api/v1/run-records/${resultUuid}/preview_html`, '_blank');
    }
  };

  // 添加拷贝功能处理函数
  const handleCopyContent = async () => {
    if (editorContent) {
      try {
        await navigator.clipboard.writeText(editorContent);
        setCopySuccess(true);
        // 3秒后重置状态
        setTimeout(() => {
          setCopySuccess(false);
        }, 3000);
      } catch (err) {
        console.error('复制失败:', err);
      }
    }
  };

  /**
   * 处理导出PDF功能
   * 通过预览接口获取HTML内容，然后调用PDF导出接口
   */
  const handleExportPDF = async () => {
    if (!resultUuid) return;

    setIsExportingPDF(true);
    setExportError('');

    try {
      // 根据环境使用不同的域名
      const baseUrl = window.location.hostname === 'ceshi.lightark.cc' 
        ? 'https://ceshi.lightark.cc' 
        : 'https://ceshi.lightark.cc';

      // 首先获取HTML内容
      const htmlResponse = await fetch(`${baseUrl}/api/v1/run-records/${resultUuid}/preview_html`);
      if (!htmlResponse.ok) {
        throw new Error('获取HTML内容失败');
      }
      
      const htmlContent = await htmlResponse.text();

      // 调用PDF导出接口
      const pdfResponse = await fetch(`${baseUrl}/api/v1/apps/html_to_pdf_screenshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/html',
        },
        body: htmlContent
      });

      if (!pdfResponse.ok) {
        throw new Error('PDF导出失败');
      }

      // 获取PDF文件并下载
      const pdfBlob = await pdfResponse.blob();
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      
      // 创建下载链接
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${appName || documentTitle || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // 清理资源
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {
      console.error('导出PDF失败:', err);
      setExportError(err instanceof Error ? err.message : '导出PDF失败');
      // 3秒后清除错误信息
      setTimeout(() => {
        setExportError('');
      }, 3000);
    } finally {
      setIsExportingPDF(false);
    }
  };

  // 在form内，input前添加文件上传相关组件
  const chatInputWithUpload = (
    <form className="chat-input" onSubmit={handleSubmit}>
      {uploadError && <div className="upload-error">{uploadError}</div>}
      {renderUploadedFiles()}
      <div className="input-container">
        <textarea
          value={inputText}
          onChange={(e) => {
            setInputText(e.target.value);
            // 简单的自动调整高度 - 只调整文本框本身
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            const newHeight = Math.min(target.scrollHeight, 300); // 最大15行高度
            target.style.height = `${newHeight}px`;
          }}
          onKeyDown={(e) => {
            // 如果按下Enter键且没有按住Shift键，则提交表单
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          placeholder={t('apps.detail.run.modal.taskInputPlaceholder', '输入问题或上传文件')}
          disabled={isLoading}
          rows={1}
          className="chat-textarea"
        />
        <div className="input-actions">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            accept={ALLOWED_FILE_EXTENSIONS.join(',')}
            disabled={isLoading || uploadedFiles.length >= MAX_FILE_COUNT}
            aria-label={t('apps.detail.uploadFile', '上传文件')}
          />
          <button
            type="button"
            className="file-button"
            onClick={handleFileButtonClick}
            disabled={isLoading || uploadedFiles.length >= MAX_FILE_COUNT}
            aria-label={t('apps.detail.uploadFile', '上传文件')}
          >
            📎
          </button>
          <button 
            type="submit" 
            className="send-button" 
            disabled={isLoading || (!inputText.trim() && uploadedFiles.length === 0)}
            aria-label={t('apps.detail.send', '发送')}
          >
            {sendButtonContent}
          </button>
        </div>
      </div>
    </form>
  );

  // 修改编辑器工具栏部分，支持在发布页面显示
  const renderEditorToolbar = () => (
    <div className="editor-toolbar">
      <div className="document-actions">
        {/* 切换按钮 - 当有resultUuid时显示 */}
        {resultUuid && (
          <button 
            className="document-action" 
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            aria-label={isPreviewMode ? t('apps.detail.showText', '显示文本') : t('apps.detail.showPreview', '显示预览')}
            title={isPreviewMode ? t('apps.detail.showText', '显示文本') : t('apps.detail.showPreview', '显示预览')}
          >
            {isPreviewMode ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 17H4v2h10v-2zm6-8H4v2h16V9zM4 15h16v-2H4v2zM4 5v2h16V5H4z" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
              </svg>
            )}
          </button>
        )}
        {/* 全屏预览按钮 - 当有resultUuid时显示 */}
        {resultUuid && (
          <button 
            className="document-action" 
            onClick={handlePreviewResult}
            aria-label={t('apps.detail.fullscreenPreview', '全屏预览')}
            title={t('apps.detail.fullscreenPreview', '全屏预览')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" fill="currentColor"/>
            </svg>
          </button>
        )}
        {/* 导出PDF按钮 - 当有resultUuid时显示 */}
        {resultUuid && (
          <button 
            className={`document-action ${isExportingPDF ? 'exporting' : ''}`} 
            onClick={handleExportPDF}
            aria-label={t('apps.detail.exportPDF', '导出PDF')}
            title={t('apps.detail.exportPDF', '导出PDF')}
            disabled={isExportingPDF}
          >
            {isExportingPDF ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="spinner">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.416" strokeDashoffset="31.416">
                  <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                  <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                </circle>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" fill="currentColor"/>
                <path d="M10,19L12,17L14,19H13V16H11V19H10Z" fill="currentColor"/>
              </svg>
            )}
          </button>
        )}
        {/* 拷贝按钮 - 当有内容时可用 */}
        <button 
          className={`document-action ${copySuccess ? 'copy-success' : ''}`} 
          onClick={handleCopyContent}
          aria-label={t('apps.detail.copyContent', '复制内容')}
          title={t('apps.detail.copyContent', '复制内容')}
          disabled={!editorContent}
        >
          {copySuccess ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
            </svg>
          )}
        </button>
      </div>
      {/* 显示导出错误信息 */}
      {exportError && (
        <div className="export-error" style={{ 
          color: '#ff4444', 
          fontSize: '12px', 
          marginTop: '8px',
          padding: '4px 8px',
          backgroundColor: '#fff5f5',
          borderRadius: '4px',
          border: '1px solid #ffcccc'
        }}>
          {exportError}
        </div>
      )}
    </div>
  );

  // 修改编辑器内容渲染部分
  const renderEditorContent = () => {
    // 如果有resultUuid且处于预览模式，显示预览iframe
    if (resultUuid && isPreviewMode) {
      const baseUrl = window.location.hostname === 'ceshi.lightark.cc' 
        ? 'https://ceshi.lightark.cc' 
        : 'https://ceshi.lightark.cc';
      return (
        <iframe
          src={`${baseUrl}/api/v1/run-records/${resultUuid}/preview_html`}
          className="preview-iframe"
          title="预览结果"
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      );
    }
    
    // 否则显示文本内容
    return editorContent ? (
      <Markdown content={editorContent} className="markdown-body" />
    ) : (
      <div className="empty-editor-message">{t('apps.detail.resultWillShowHere', '最终结果将显示在这里...')}</div>
    );
  };

  return (
    standaloneMode ? (
      <Layout hideFooter={true} hideHeader={true}>
        <div className={`gemini-container ${isRunMode ? 'run-mode' : ''}`}>
          {/* 左侧聊天区域 - 显示思考过程 */}
          <div className="chat-panel">
            <div className="gemini-header">
              <div className="model-selector">
                <span className="gemini-logo">{appName || 'Agent'}</span>
                {appId && (
                  <button className="back-button" onClick={handleBackToDetail}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
                    </svg>
                    {t('apps.detail.backToDetail', '返回应用详情')}
                  </button>
                )}
                <div className="model-dropdown">
                  {isRunMode ? (
                    <LanguageSelector />
                  ) : (
                    <>
                      <span>2.0 Flash</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M7 10l5 5 5-5z" fill="currentColor"></path>
                      </svg>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="chat-messages">
              {messages.map((message, index) => (
                <div key={index} className={`chat-message ${message.type}`}>
                  {message.type === 'log' && (
                    <div className="message-title">
                      <span className="document-title">{t('apps.detail.thinking', '思考过程')}</span>
                      {message.timestamp && <span className="message-time">{message.timestamp}</span>}
                    </div>
                  )}
                  <div className="message-content">
                    {message.type === 'log' ? (
                      <Markdown content={message.content} className="markdown-body chat-markdown" />
                    ) : (
                      <>
                        {message.content}
                        {message.files && message.files.length > 0 && (
                          <div className="message-files">
                            {message.files.map(file => (
                              <div key={file.temp_file_id} className="message-file">
                                <span className="file-icon">{getFileIcon(file.file_type)}</span>
                                <span className="file-name">{file.file_name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chat-message log">
                  <div className="message-title">
                    <span className="document-title">{t('apps.detail.thinkingNow', '思考中...')}</span>
                  </div>
                  <div className="message-content loading-message">
                    <div className="thinking-indicator">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="chat-input-container">
              {chatInputWithUpload}
            </div>
          </div>
          
          {/* 使用Markdown组件渲染结果内容，支持代码高亮、数学公式、表格等 */}
          <div className="editor-panel">
            {renderEditorToolbar()}
            
            <div className="editor-content">
              {renderEditorContent()}
            </div>
          </div>
        </div>
      </Layout>
    ) : (
      <div className={`gemini-container embedded-mode canvas-chat-page ${isRunMode ? 'run-mode' : ''}`}>
        {/* 左侧聊天区域 - 显示思考过程 */}
        <div className="chat-panel">
          <div className="gemini-header">
            <div className="model-selector">
              <span className="gemini-logo">{appName || 'Agent'}</span>
              {appId && (
                <button className="back-button" onClick={handleBackToDetail}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
                  </svg>
                  {t('apps.detail.backToDetail', '返回应用详情')}
                </button>
              )}
              <div className="model-dropdown">
                {isRunMode ? (
                  <LanguageSelector />
                ) : (
                  <>
                    <span>2.0 Flash</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M7 10l5 5 5-5z" fill="currentColor"></path>
                    </svg>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="chat-messages">
            {messages.map((message, index) => (
              <div key={index} className={`chat-message ${message.type}`}>
                {message.type === 'log' && (
                  <div className="message-title">
                    <span className="document-title">{t('apps.detail.thinking', '思考过程')}</span>
                    {message.timestamp && <span className="message-time">{message.timestamp}</span>}
                  </div>
                )}
                <div className="message-content">
                  {message.type === 'log' ? (
                    <Markdown content={message.content} className="markdown-body chat-markdown" />
                  ) : (
                    <>
                      {message.content}
                      {message.files && message.files.length > 0 && (
                        <div className="message-files">
                          {message.files.map(file => (
                            <div key={file.temp_file_id} className="message-file">
                              <span className="file-icon">{getFileIcon(file.file_type)}</span>
                              <span className="file-name">{file.file_name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message log">
                <div className="message-title">
                  <span className="document-title">{t('apps.detail.thinkingNow', '思考中...')}</span>
                </div>
                <div className="message-content loading-message">
                  <div className="thinking-indicator">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
            {error && <div className="error-message">{error}</div>}
            
          <div className="chat-input-container">
            {chatInputWithUpload}
          </div>
        </div>
        
        {/* 使用Markdown组件渲染结果内容，支持代码高亮、数学公式、表格等 */}
        <div className="editor-panel">
          {renderEditorToolbar()}
          
          <div className="editor-content">
            {renderEditorContent()}
          </div>
        </div>
      </div>
    )
  );
};

export default CanvasChat; 